import { useEffect, useState } from 'react'

import Action from 'ageas-react-component-action'
import Dropdown from 'ageas-react-component-dropdownlist'
import Link from 'ageas-react-component-link'
import MainLoader from 'ageas-react-component-loader'
import Section from 'ageas-react-component-section'

import { MatchedEntity } from '../../@types/PolicyHolderDetails'
import { usePolicyholderDetailsStore } from '../../Stores/policyholderDetailsStore'
import { useSimulatorStore } from '../../Stores/simulatorStore'
import { useCreateProposalStore } from '../../Stores/useCreateProposalStore'
import { useResumeStore } from '../../Stores/useResumeStore'
import { useWarningStore } from '../../Stores/warningStore'
import { ADRUM_EVENT } from '../../constants/adrumEvent'
import { WARNING_BAR } from '../../constants/warningBar'
import { useAddressesUpdateEntityPayload } from '../../hooks/api/updateEntityDetails/useAddressesUpdateEntityPayload'
import { useUpdateEntityDetails } from '../../hooks/api/updateEntityDetails/useUpdateEntityDetails'
import { useGetPolicyholderDetails } from '../../hooks/api/useGetPolicyholderDetails'
import { useAdrum } from '../../hooks/useAdrum'
import { t } from '../../i18n'
import { Disclaimer } from '../Disclaimer'
import {
  AddressForm,
  FormAddressValuesProps,
  initialFormAddressValues,
} from './components/AddressForm'
import { ButtonSaveContainer } from './style'

export const Addresses = () => {
  const [enableAdrum, setEnableAdrum] = useState(true)
  const [showAddress, setShowAddress] = useState(false)
  const [isAddressAdded, setIsAddressAdded] = useState(false)
  const [updateAddressSelected, setUpdateAddressSelected] = useState(false)
  const { isResume } = useResumeStore()
  const [enableGetPolicyholderRequest, setEnableGetPolicyholderRequest] =
    useState(!isResume)
  const [formValues, setFormValues] = useState<FormAddressValuesProps>(
    initialFormAddressValues,
  )
  const {
    policyholderDetailsSelected,
    policyholderAddressSelected,
    setPolicyholderDetails,
    setPolicyholderDetailsSelected,
    setPolicyholderAddressSelected,
  } = usePolicyholderDetailsStore()
  const { entitySelectedAddress } = useSimulatorStore()
  const { setWarningOptions } = useWarningStore()
  const { handleAddressesPayload } = useAddressesUpdateEntityPayload()
  const { createProposal } = useCreateProposalStore()

  const { startADRUMTagging } = useAdrum(!enableAdrum)

  const {
    data: getPolicyholderData,
    isFetching: getPolicyholderisFetching,
    isSuccess: getPolicyholderIsSuccess,
  } = useGetPolicyholderDetails(
    {
      vatNumber: policyholderDetailsSelected?.vatNumber || null,
      entityType: 'individual',
      idExternalType:
        createProposal?.createProposalResponse?.policyHolderData
          ?.idExternalType || '',
    },
    enableGetPolicyholderRequest,
  )

  const formattedAddress = policyholderDetailsSelected?.addresses?.map(
    (address) => ({
      caption:
        (address?.typology?.postalAddress?.fullAddress || '')?.length === 0
          ? `${address.postalCode} ${address.postalCodeDescription}`
          : address?.typology?.postalAddress?.fullAddress || '',
      value: address?.locationId || '',
    }),
  )

  const { mutate, isSuccess, isLoading } = useUpdateEntityDetails()

  const handleAddAddress = (value: string) => {
    const newAddress = policyholderDetailsSelected?.addresses?.find(
      (item) => item.locationId === value,
    )

    setPolicyholderAddressSelected(newAddress!)
  }

  const handleUpdateAddress = (entity: MatchedEntity) => {
    const addressId =
      policyholderAddressSelected?.locationId || entitySelectedAddress?.sequence

    if (addressId && !isAddressAdded) {
      handleAddAddress(addressId)
    } else {
      const currentEntityAddressLocationId =
        entity?.addresses?.[entity?.addresses.length - 1]?.locationId!
      handleAddAddress(currentEntityAddressLocationId)
    }

    setUpdateAddressSelected(true)

    setTimeout(() => {
      setIsAddressAdded(false)
    }, 100)
  }

  useEffect(() => {
    if (isSuccess) {
      setShowAddress(false)
      setEnableGetPolicyholderRequest(true)
      setIsAddressAdded(true)
      setWarningOptions({
        type: WARNING_BAR.type.success,
        labelTypes: [t('pages.step3.success.update')],
      })
    }
  }, [isSuccess, setWarningOptions])

  useEffect(() => {
    if (getPolicyholderIsSuccess && enableGetPolicyholderRequest) {
      setPolicyholderDetails(getPolicyholderData)

      const entity = getPolicyholderData?.matchedIndividualEntities?.find(
        (item) => item.idEntity === policyholderDetailsSelected?.idEntity,
      )

      handleUpdateAddress(entity!)
      setPolicyholderDetailsSelected(entity!)
      setEnableGetPolicyholderRequest(false)
    }
  }, [
    entitySelectedAddress,
    getPolicyholderData,
    getPolicyholderIsSuccess,
    policyholderDetailsSelected,
    setPolicyholderDetails,
    updateAddressSelected,
    setPolicyholderDetailsSelected,
  ])

  useEffect(() => {
    if (enableAdrum && isSuccess) {
      startADRUMTagging(ADRUM_EVENT.address)
      setEnableAdrum(false)
    }
  }, [enableAdrum, isSuccess])

  return (
    <>
      <MainLoader show={isLoading || getPolicyholderisFetching} />
      <Dropdown
        id={`entityDetailsTaxAddress-${getPolicyholderisFetching}-${updateAddressSelected}-${isAddressAdded}`}
        key={`entityDetailsTaxAddress-${getPolicyholderisFetching}-${updateAddressSelected}-${isAddressAdded}`}
        name="entityDetailsTaxAddress"
        label={t('pages.step3.addresses.fiscalAddress')}
        colorTheme="blue"
        value={
          formattedAddress?.find(
            (item) => item.value === policyholderAddressSelected?.locationId,
          )?.caption ||
          formattedAddress?.[0].caption ||
          ''
        }
        options={formattedAddress}
        onValueChanged={(id: any, value: any) => handleAddAddress(value)}
        version="2"
      />
      {showAddress && !isLoading && (
        <>
          <Section
            id="addressesTitle"
            name={t('pages.step3.addresses.newAddress')}
            config={{ labelSize: '20px', labelBold: '500' }}
            colorTheme="blue"
          />
          <AddressForm
            hiddenTitle
            initialFormValues={formValues}
            onChange={(values) => setFormValues(values)}
          />
          <Disclaimer text={t('addressSelectTable.disclaimerInfo')} />
          <Disclaimer text={t('addressSelectTable.disclaimerFont')} />
          <div className="add-content">
            <Link
              label={t('general.cancel')}
              onValueChanged={() => setShowAddress(false)}
            />
          </div>
          <ButtonSaveContainer>
            <div className="button-save">
              <Action
                id="addressAction"
                label={t('general.save')}
                type="button"
                disable={(formValues.entityLocalization || '')?.length === 0}
                onValueChanged={() =>
                  mutate({
                    payload: handleAddressesPayload(formValues),
                    type: 'addresses',
                  })
                }
              />
            </div>
          </ButtonSaveContainer>
        </>
      )}
      {!showAddress && (
        <div className="add-content">
          <Link
            label={t('pages.step3.addresses.addAddress')}
            onValueChanged={() => {
              setUpdateAddressSelected(false)
              setIsAddressAdded(false)
              setFormValues(initialFormAddressValues)
              setShowAddress(true)
            }}
          />
        </div>
      )}
    </>
  )
}
